<template>
  <div v-if="bus.edit_mode"
       class="sku-candidate-pool">
    <div class="pool-button"
         @click="showDrawer = true">
      <span class="count">
        <i v-if="SkuCandidatePool.isLoading" class="el-icon-loading" />
        <b v-else>{{ componentBus.skuCandidateSize }}</b>
      </span>
      <span class="text">{{ $t('POOL') }}</span>
    </div>

    <el-drawer :visible.sync="showDrawer"
               class="sku-candidate-pool__drawer"
               :with-header="false"
               size="60%"
               @closed="onClosed">
      <el-container dir="horizontal">
        <el-header class="sku-candidate-pool__drawer-header">
          <span class="title">{{ $t('NEW SKU ENROLLMENT POOL') }}</span>
          <div class="actions">
            <el-button :class="{'inactive' : isAllCandidateSelected}"
                       :disabled="isAllCandidateSelected"
                       @click="selectAll">
              {{ $t('SELECT ALL') }}
            </el-button>
            <el-button :class="{'inactive' : isAllCanditeUnSelected}"
                       :disabled="isAllCanditeUnSelected"
                       @click="deselectAll">
              {{ $t('DESELECT ALL') }}
            </el-button>
          </div>
        </el-header>
        <el-main class="sku-candidate-pool__drawer-main">
          <div class="items">
            <HorizontalLoading v-show="SkuCandidatePool.isLoading" />

            <div class="content">
              <div v-for="(item,index) in SkuCandidatePool.data"
                   :key="index"
                   :class="{ 'selected': item.completed }"
                   class="item"
                   @click="item.completed = !item.completed">
                <img :src="item.encoded_image">
                <el-button icon="mdi mdi-close"
                           circle
                           type="danger"
                           @click.stop="discardSkuCandidate(item)" />
              </div>
            </div>
          </div>
        </el-main>
        <el-footer class="sku-candidate-pool__drawer-footer">
          <el-button type="danger"
                     :disabled="isProposeButtonDisabled"
                     @click="discardSelectedSkuCandidate">
            {{ $t('Remove All') }}
          </el-button>
          <el-button type="primary"
                     :loading="isProposing"
                     :disabled="isProposeButtonDisabled"
                     @click="proposeForEnrollment">
            {{ $t('Propose for Enrollment') }}
          </el-button>
        </el-footer>
      </el-container>
    </el-drawer>
  </div>
</template>

<script>
import { createCrudState } from 'vuex-module-generator';
import { Message } from 'element-ui';
import componentBus, { EVENTS } from './componentBus';
import {
  postSkuCandidate,
  fetchSkuCandidatePool,
  proposeForEnrollment,
  discardSkuCandidate,
  candidateBulkDelete,
  candidateBulkCreate
} from '../../../shared/apis';
import bus from '../sku-validation-scene/bus';

export default {
  name: 'SkuCandidatePool',
  props: {
    projectId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      bus,
      componentBus,
      isProposing: false,
      showDrawer: this.visible,
      SkuCandidatePool: createCrudState().list
    };
  },
  computed: {
    isProposeButtonDisabled() {
      return !this.SkuCandidatePool.data.filter(item => item.completed).length;
    },
    isAllCandidateSelected() {
      return this.SkuCandidatePool.data.every(obj => obj.completed === true);
    },
    isAllCanditeUnSelected() {
      return this.SkuCandidatePool.data.every(obj => obj.completed === false);
    }
  },
  mounted() {
    this.fetchData();
    componentBus.$on(EVENTS.POST_SKU_CANDIDATE, this.postSkuCandidate);
    componentBus.$on(EVENTS.POST_BULK_SKU_CANDIDATE, this.postSkuBulkCandidate);
  },
  beforeDestroy() {
    componentBus.$off(EVENTS.POST_SKU_CANDIDATE, this.postSkuCandidate);
    componentBus.$off(EVENTS.POST_BULK_SKU_CANDIDATE, this.postSkuBulkCandidate);
  },
  methods: {
    onClosed() {
      this.$emit('update:visible', false);
    },
    async discardSelectedSkuCandidate() {
      const ids = this.SkuCandidatePool.data.filter(i => i.completed).map(i => i.id);

      await candidateBulkDelete(this.projectId, { data: { ids } })
        .then(() => {
          this.fetchData();
        });

      Message(this.$t('Successfuly discarded'));
    },
    discardSkuCandidate(item) {
      discardSkuCandidate(this.projectId, item.id)
        .then(() => {
          Message(this.$t('Successfuly discarded'));
          this.fetchData();
        });
    },
    selectAll() {
      this.SkuCandidatePool.data.forEach((acc) => {
        acc.completed = true;
      });
    },
    deselectAll() {
      this.SkuCandidatePool.data.forEach((acc) => {
        acc.completed = false;
      });
    },
    proposeForEnrollment() {
      this.isProposing = true;
      const ids = this.SkuCandidatePool.data.filter(item => item.completed).map(item => item.id);
      proposeForEnrollment(this.projectId, {
        ids,
        sku_candidate: {
          completed: true
        }
      }).then(() => {
        Message(this.$t('Pool successfuly submitted'));
        this.fetchData();
      }).finally(() => {
        this.isProposing = false;
      });
    },
    postSkuCandidate({ encodedImage }) {
      componentBus.isSkuCandidateCreating = true;
      postSkuCandidate(this.projectId, {
        sku_candidate: {
          completed: false,
          encoded_image: encodedImage
        }
      })
        .then(() => {
          Message(this.$t('Successfuly added to pool'));

          this.fetchData();
        })
        .finally(() => {
          componentBus.isSkuCandidateCreating = false;
        });
    },
    postSkuBulkCandidate(images) {
      componentBus.isSkuCandidateCreating = true;
      candidateBulkCreate(this.projectId, {
        images
      })
        .then(() => {
          this.fetchData();
        })
        .finally(() => {
          componentBus.isSkuCandidateCreating = false;
        });
    },
    async fetchData() {
      this.SkuCandidatePool.isLoading = true;
      this.SkuCandidatePool.hasError = false;

      try {
        const { data: { data } } = await fetchSkuCandidatePool(this.projectId);
        componentBus.skuCandidateSize = data.length;
        this.SkuCandidatePool.data = data;
        this.SkuCandidatePool.isLoaded = true;
      } catch (error) {
        this.SkuCandidatePool.hasError = true;
      }

      this.SkuCandidatePool.isLoading = false;
    }
  }
};
</script>

<style lang="scss">
.sku-candidate-pool {
  &__drawer {
    &-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 50px 50px 10px 50px;

      .title {
        font-size: 26px;
        font-style: normal;
        font-weight: 400;
        line-height: 55px;
        color: #3b3b3b;
        letter-spacing: 1.326px;
      }

      .actions {
        display: flex;
        gap: 10px;
        align-items: center;
        justify-content: center;
        padding: 12px 20px;

        .el-button {
          padding: 12px 20px;
          font-weight: 600;
          color: #409eff;
          border: 2px solid #409eff;
          border-radius: 3px;

          &.inactive {
            opacity: 0.5;
          }

          &:focus {
            background-color: $white;
          }
        }
      }
    }

    &-main {
      padding: 20px 30px;
    }

    &-footer {
      height: auto !important;
      padding: 20px;
      text-align: center;

      .el-button {
        padding: 12px 40px;
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        text-transform: uppercase;
      }
    }

    .el-drawer {
      .el-container {
        height: 100vh;
        background-color: $baiSeWhite;
      }

      .el-main {
        overflow-y: auto;
      }

      .items {
        position: relative;
        box-sizing: border-box;
        flex: 1;
        height: 100%;
        padding: 10px;
        overflow-y: auto;

        .content {
          display: flex;
          flex-wrap: wrap;
        }

        .item {
          position: relative;
          padding: 10px;
          margin: 10px;
          cursor: pointer;
          background-color: $white;
          border-radius: 4px;

          &.selected {
            outline: 3px dotted red;
          }

          .el-button {
            position: absolute;
            top: -10px;
            right: -10px;
            z-index: 3;
            display: none;
          }

          &:hover {
            .el-button {
              display: block;
            }
          }

          img {
            display: block;
            max-width: 200px;
            max-height: 200px;
            margin: auto;
          }
        }
      }
    }
  }

  .pool-button {
    position: fixed;
    top: 50%;
    right: -40px;
    width: 140px;
    height: 50px;
    font-size: 20px;
    color: $white;
    cursor: pointer;
    background-color: $andreaBlue;
    transform: rotate(90deg);

    span {
      position: absolute;
    }

    .count {
      position: absolute;
      top: 50%;
      left: 10px;
      width: 35px;
      height: 35px;
      line-height: 35px;
      text-align: center;
      background-color: rgba($white, 0.4);
      border-radius: 50%;
      transform: rotate(-90deg) translateX(50%);
    }

    .text {
      position: absolute;
      top: 50%;
      right: 20px;
      transform: rotate(180deg) translateY(50%);
    }
  }
}
</style>
